import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ApiService } from '@/shared/services/api.service';
import { useNotificationStore } from './notification.store';

export interface PolicyData {
  pid: string;
  scope: 'organization' | 'floor';
  scope_name: string;
  active: boolean;
  scope_id: string;
  scope_pid: string;
  exceptions?: {
    users: string[];
    [key: string]: any;
  };
  policy: {
    pid: string;
    key: string;
    name: string;
    description: string;
  };
}

export interface OrgSettingData {
  pid: string;
  key: string;
  name: string;
  description: string | null;
  created: string;
  settings?: Record<string, any>;
}

interface AdministrationStore {
  selectedModule: any | null;
  selectedBuilding: any | null;
  selectedFloor: any | null;
  selectedLocation: any | null;
  showAddEditUser: boolean;
  showAddEditUserRole: boolean;
  showResetPassword: boolean;
  selectedUser: any | null;
  showAddEditFloor: boolean;
  showAddEditBuilding: boolean;
  showAddEditLocation: boolean;
  showAddEditPolicy: boolean;
  policies: PolicyData[];
  policiesLoading: boolean;
  policiesLoadError: boolean;
  policiesLoadedInSession: boolean;
  selectedPolicy: PolicyData | null;
  showAddEditSetting: boolean;
  orgSettings: OrgSettingData[];
  orgSettingsLoading: boolean;
  selectedOrgSetting: OrgSettingData | null;

  setSelectedModule: (moduleName: string) => void;
  setSelectedBuilding: (building: any) => void;
  setSelectedFloor: (floor: any) => void;
  setSelectedLocation: (location: any) => void;
  setShowAddEditUser: (open: boolean) => void;
  setShowAddEditUserRole: (open: boolean) => void;
  setShowResetPassword: (open: boolean) => void;
  setSelectedUser: (user: any) => void;
  setShowAddEditFloor: (open: boolean) => void;
  setShowAddEditBuilding: (open: boolean) => void;
  setShowAddEditLocation: (open: boolean) => void;
  setShowAddEditPolicy: (open: boolean) => void;
  setPolicies: (policies: PolicyData[]) => void;
  setPoliciesLoading: (loading: boolean) => void;
  setSelectedPolicy: (policy: PolicyData | null) => void;
  setShowAddEditSetting: (open: boolean) => void;
  setOrgSettings: (settings: OrgSettingData[]) => void;
  setOrgSettingsLoading: (loading: boolean) => void;
  setSelectedOrgSetting: (setting: OrgSettingData | null) => void;
  fetchPolicies: () => Promise<void>;
  deletePolicy: (policyPid: string) => Promise<boolean>;
}

export const useAdministrationStore = create<AdministrationStore>()(
  persist(
    (set) => ({
      selectedModule: null,
      selectedBuilding: null,
      selectedFloor: null,
      selectedLocation: null,
      showAddEditUser: false,
      showAddEditUserRole: false,
      showResetPassword: false,
      selectedUser: null,
      showAddEditFloor: false,
      showAddEditBuilding: false,
      showAddEditLocation: false,
      showAddEditPolicy: false,
      policies: [],
      policiesLoading: false,
      policiesLoadError: false,
      policiesLoadedInSession: false,
      selectedPolicy: null,
      showAddEditSetting: false,
      orgSettings: [],
      orgSettingsLoading: false,
      selectedOrgSetting: null,

      setSelectedModule: (moduleName: string) => set({ selectedModule: moduleName }),
      setSelectedBuilding: (building: string) => set({ selectedBuilding: building }),
      setSelectedFloor: (floor: string) => set({ selectedFloor: floor }),
      setShowAddEditUser: (open: boolean) => set({ showAddEditUser: open }),
      setShowAddEditUserRole: (open: boolean) => set({ showAddEditUserRole: open }),
      setSelectedLocation: (open: boolean) => set({ selectedLocation: open }),
      setShowResetPassword: (open: boolean) => set({ showResetPassword: open }),
      setSelectedUser: (user: boolean) => set({ selectedUser: user }),
      setShowAddEditFloor: (open: boolean) => set({ showAddEditFloor: open }),
      setShowAddEditBuilding: (open: boolean) => set({ showAddEditBuilding: open }),
      setShowAddEditLocation: (open: boolean) => set({ showAddEditLocation: open }),
      setShowAddEditPolicy: (open: boolean) => set({ showAddEditPolicy: open }),
      setPolicies: (policies: PolicyData[]) => set({ policies }),
      setPoliciesLoading: (loading: boolean) => set({ policiesLoading: loading }),
      setSelectedPolicy: (policy: PolicyData | null) => set({ selectedPolicy: policy }),
      setShowAddEditSetting: (open: boolean) => set({ showAddEditSetting: open }),
      setOrgSettings: (settings: OrgSettingData[]) => set({ orgSettings: settings }),
      setOrgSettingsLoading: (loading: boolean) => set({ orgSettingsLoading: loading }),
      setSelectedOrgSetting: (setting: OrgSettingData | null) =>
        set({ selectedOrgSetting: setting }),
      fetchPolicies: async () => {
        const RETRY_DELAYS = [2000, 5000, 10000];

        const attempt = async (retryIndex: number): Promise<void> => {
          try {
            set({ policiesLoading: true });
            const response = await ApiService.get<{ items: PolicyData[]; total: number }>(
              '/admin/policies?page=1&limit=1000'
            );
            set({
              policies: response?.items || [],
              policiesLoadError: false,
              policiesLoadedInSession: true,
            });
          } catch (error) {
            console.error(
              `Error fetching policies (attempt ${retryIndex + 1}/${RETRY_DELAYS.length + 1}):`,
              error
            );

            if (retryIndex < RETRY_DELAYS.length) {
              // Schedule next retry — non-blocking so bootstrap is not delayed
              setTimeout(() => attempt(retryIndex + 1), RETRY_DELAYS[retryIndex]);
            } else {
              // All retries exhausted — treat as no active policies so the UI unblocks.
              // policiesLoadedInSession=true releases the loading gate; policies=[] means
              // no restrictions are enforced rather than applying a stale cached state.
              set({ policiesLoadError: true, policiesLoadedInSession: true });
              useNotificationStore.getState().notify({
                title: 'Policy Warning',
                message:
                  'Unable to load the latest policy settings. Policy restrictions are not currently enforced. Please refresh to try again.',
                severity: 'warning',
              });
            }
          } finally {
            set({ policiesLoading: false });
          }
        };

        await attempt(0);
      },
      deletePolicy: async (policyPid: string) => {
        try {
          const response = await ApiService.delete(`/admin/policies/${policyPid}`);

          if (response !== undefined) {
            // Remove the deleted policy from the store
            set((state) => ({
              policies: state.policies.filter((policy) => policy.pid !== policyPid),
              selectedPolicy: state.selectedPolicy?.pid === policyPid ? null : state.selectedPolicy,
            }));
            return true;
          }
          return false;
        } catch (error) {
          console.error('Error deleting policy:', error);
          return false;
        }
      },
    }),
    {
      name: 'administration-store',
      partialize: (state) => ({
        // policies intentionally excluded — stale cached policies must never be applied
        // on login before a fresh fetch completes. policiesLoadedInSession (not persisted)
        // gates all policy validation so consumers wait for the live server response.
        selectedPolicy: state.selectedPolicy,
      }),
    }
  )
);
